'use client';

import React, { useState, useEffect, useRef, useMemo } from 'react';
import Text from '@components/atoms/Text';
import Image from 'next/image';
import styles from './ContentSection.module.scss';
import assetLoader from '@utils/internalImageLoder';
import { useIsMobile } from '@hooks/use-mobile';
import Button from '@components/atoms/Button';
import { useRouter } from 'next/navigation';
import { useGlobalLoader } from '@hooks/useGlobalLoader';
import VideoModalWrapper from '@components/molecules/VideoModalWrapper';
import { getMonthFromDate } from '@helpers/dateFormatter';
import lottie from 'lottie-web';
import type { AnimationItem } from 'lottie-web';
import winnerCelebrateAnimation from './winner-celebrate.json';
import { Modal } from '@components/atoms/Modal';
import { postLead } from '@services/leadCaptureService';
import { getCityFromIp } from '@utils/cityDetection';
import { postIntentCaptured } from '@services/carDropService';
import { EventType, trackEventWithCommonProps } from '@utils/eventTracker';
import { PAGE_TYPES } from '@constants/pageTypes';


const ContentSection = ({ 
    userId, 
    isLoggedIn, 
    cityName, 
    activeCampaignData, 
    completedCampaignData, 
    isUserParticipated, 
    allCompletedCampaigns, 
    userProfile, 
    isIntentCaptured, 
    contestState 
}: any) => {
    
    const router = useRouter();
    const isMobile = useIsMobile();
    const { showLoader } = useGlobalLoader();
    const [isWinnerRevealed, setIsWinnerRevealed] = useState(true);
    // const [shouldShowAnimation, setShouldShowAnimation] = useState(false);
    // const [showCelebration, setShowCelebration] = useState(false);
    // const [showLiveModalMobile, setShowLiveModalMobile] = useState(false);
    // const [showPlanningModal, setShowPlanningModal] = useState(true);
    // const [selectedPlanningOption, setSelectedPlanningOption] = useState<string>('');

    // const planningOptions = [
    //     { id: 'next_3_months', label: 'Within the next 3 months' },
    //     { id: 'next_6_months', label: 'Within the next 6 months' },
    //     { id: 'not_any_time_soon', label: 'Not any time soon' }
    // ];

    const [selectedCampaignIndex, setSelectedCampaignIndex] = useState<number>(0);

    const sortedCompletedCampaigns = useMemo(() => {
        if (!allCompletedCampaigns || allCompletedCampaigns.length === 0) return [];
        return [...allCompletedCampaigns].sort((a: any, b: any) => {
            const dateA = new Date(a?.startDate || 0).getTime();
            const dateB = new Date(b?.startDate || 0).getTime();
            return dateB - dateA;
        });
    }, [allCompletedCampaigns]);

    const completedCampaign = sortedCompletedCampaigns?.[0] || null;

    const selectedCampaign = useMemo(() => {
        return sortedCompletedCampaigns?.[selectedCampaignIndex] || completedCampaign;
    }, [sortedCompletedCampaigns, selectedCampaignIndex, completedCampaign]);

    const selectedWinnerName = selectedCampaign?.metadata?.winnerName || '';
    const selectedWinnerCity = selectedCampaign?.metadata?.winnerCity || '';
    const selectedCashbackWinnerData = selectedCampaign?.metadata?.cashBackWinners || [];

    const isSelectedCampaignPreviousMonth = useMemo(() => {
        if (!selectedCampaign?.startDate) return false;
        
        const today = new Date();
        const previousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const campaignDate = new Date(selectedCampaign.startDate);
        
        return (
            campaignDate.getMonth() === previousMonth.getMonth() &&
            campaignDate.getFullYear() === previousMonth.getFullYear()
        );
    }, [selectedCampaign?.startDate]);

    const selectedVideoUrl = useMemo(() => {
        const monthName = getMonthFromDate(selectedCampaign?.startDate);
        if (monthName === "November") {
            return "https://ackodrive-prod.ackoassets.com/video/generic/nov_car_drop_winner_vertical.m3u8";
        } else {
            return "https://ackodrive-prod.ackoassets.com/video/generic/dec_car_drop_winner_vertical.m3u8";
        }
    }, [selectedCampaign?.startDate]);

    const selectedDesktopVideoUrl = useMemo(() => {
        const monthName = getMonthFromDate(selectedCampaign?.startDate);
        if (monthName === "December") {
            return "https://ackodrive-prod.ackoassets.com/video/generic/dec_car_drop_winner_horizontal.m3u8";
        }
        return undefined;
    }, [selectedCampaign?.startDate]);

    const selectedMetaData = useMemo(() => {
        const monthName = getMonthFromDate(selectedCampaign?.startDate);
        if (monthName === "November") {
            return {
                authorName: "ACKO DRIVE",
                authorType: "ACKO DRIVE",
                duration: "127",
                mediaSource: "S3",
                primaryText: "ACKO Drive Car Drop Winner",
                secondaryText: "",
                thumbnail: `${assetLoader()}/images/winnerThumbnail.png`,
                title: "ACKO Drive Car Drop Winner",
            };
        } else {
            return {
                authorName: "ACKO DRIVE",
                authorType: "ACKO DRIVE",
                duration: "127",
                mediaSource: "S3",
                primaryText: "ACKO Drive Car Drop Winner",
                secondaryText: "",
                thumbnail: isMobile ? `${assetLoader()}/images/CarDrop/winnerWithKey.png` : `${assetLoader()}/images/CarDrop/winnerWithKeyDesktop.png`,
                title: "ACKO Drive Car Drop Winner",
            };
        }
    }, [selectedCampaign?.startDate, isMobile]);

    const selectedCashbackAmount = useMemo(() => {
        const monthName = getMonthFromDate(selectedCampaign?.startDate);
        return monthName === "December" ? "₹1 lakh" : "₹2 lakh";
    }, [selectedCampaign?.startDate]);

    const previousToPreviousMonthCampaign = useMemo(() => {
        if (!allCompletedCampaigns || allCompletedCampaigns.length === 0) return null;

        const now = new Date();
        let targetMonth = now.getMonth() - 2;
        let targetYear = now.getFullYear();
        
        if (targetMonth < 0) {
            targetMonth += 12;
            targetYear -= 1;
        }

        return allCompletedCampaigns.find((campaign: any) => {
            const revealDate = campaign?.metadata?.productRevealDate;
            if (!revealDate) return false;

            const reveal = new Date(revealDate);
            return reveal.getMonth() === targetMonth && reveal.getFullYear() === targetYear;
        }) || null;
    }, [allCompletedCampaigns]);

    const previousToPreviousMonthCashbackWinners = previousToPreviousMonthCampaign?.metadata?.cashBackWinners || [];

    // const winnerSectionRef = useRef<HTMLDivElement>(null);
    // const lottieLeftRef = useRef<HTMLDivElement>(null);
    // const lottieRightRef = useRef<HTMLDivElement>(null);
    // const lottieLeftAnimationRef = useRef<AnimationItem | null>(null);
    // const lottieRightAnimationRef = useRef<AnimationItem | null>(null);
    
    // const WINNER_REVEALED_KEY = 'carDropWinnerRevealed';
    
    // useEffect(() => {
    //     const checkRevealedStatus = () => {
    //         try {
    //             const storedData = localStorage.getItem(WINNER_REVEALED_KEY);
    //             if (storedData) {
    //                 const { revealed, expiry } = JSON.parse(storedData);
    //                 const now = new Date().getTime();
                    
    //                 if (revealed && expiry && now < expiry) {
    //                     setIsWinnerRevealed(true);
    //                     setShouldShowAnimation(false);
    //                     setShowLiveModalMobile(true)
    //                     return;
    //                 } else {
    //                     localStorage.removeItem(WINNER_REVEALED_KEY);
    //                 }
    //             }
    //             setIsWinnerRevealed(false);
    //         } catch {
    //             setIsWinnerRevealed(false);
    //         }
    //     };
        
    //     checkRevealedStatus();
    // }, []);

    // useEffect(() => {
    //     if (isMobile && !isWinnerRevealed) {
    //         document.body.style.overflow = 'hidden';
    //         return () => {
    //             document.body.style.overflow = '';
    //         };
    //     }
    // }, [isMobile, isWinnerRevealed]);

    // useEffect(() => {
    //     if (showCelebration) {
    //         if (lottieLeftRef.current) {
    //             lottieLeftAnimationRef.current = lottie.loadAnimation({
    //                 container: lottieLeftRef.current,
    //                 renderer: 'svg',
    //                 loop: false,
    //                 autoplay: true,
    //                 animationData: winnerCelebrateAnimation,
    //             });

    //             lottieLeftAnimationRef.current.addEventListener('complete', () => {
    //                 setShowCelebration(false);
    //             });
    //         }

    //         if (lottieRightRef.current) {
    //             lottieRightAnimationRef.current = lottie.loadAnimation({
    //                 container: lottieRightRef.current,
    //                 renderer: 'svg',
    //                 loop: false,
    //                 autoplay: true,
    //                 animationData: winnerCelebrateAnimation,
    //             });
    //         }

    //         return () => {
    //             if (lottieLeftAnimationRef.current) {
    //                 lottieLeftAnimationRef.current.destroy();
    //             }
    //             if (lottieRightAnimationRef.current) {
    //                 lottieRightAnimationRef.current.destroy();
    //             }
    //         };
    //     }
    // }, [showCelebration]);

    // const shouldShowPlanningModal = !isIntentCaptured && contestState === "ALREADY_PARTICIPATED" && isLoggedIn && showPlanningModal;

    // useEffect(() => {
    //     if (shouldShowPlanningModal) {
    //         (async () => {
    //             await trackEventWithCommonProps("view", EventType.PAGE_EVENT, {
    //                 pageType: PAGE_TYPES.CAR_DROP_PAGE,
    //                 additionalProps: {
    //                     logged_in: isLoggedIn ? 'Yes' : 'No',
    //                     phone: userProfile?.phone || userProfile?.contact_no,
    //                     campaign_name: `${getMonthFromDate(activeCampaignData?.startDate)} Car Drop`,
    //                     view_type: 'view_page',
    //                     view_name: 'view_car_drop_intent',
    //                     city_name: cityName,
    //                 },
    //             });
    //         })();
    //     }
    // }, [shouldShowPlanningModal]);

    // const handleRevealWinner = () => {
    //     try {
    //         const winnerRevealDate = activeCampaignData?.metadata?.winnerRevealDate;
    //         let expiryTime: number;
            
    //         if (winnerRevealDate) {
    //             const revealDate = new Date(winnerRevealDate);
    //             expiryTime = revealDate.getTime() - (24 * 60 * 60 * 1000);
    //         } else {
    //             expiryTime = new Date().getTime() + (7 * 24 * 60 * 60 * 1000);
    //         }
            
    //         localStorage.setItem(WINNER_REVEALED_KEY, JSON.stringify({
    //             revealed: true,
    //             expiry: expiryTime
    //         }));
    //     } catch {
    //     }
        
    //     setIsWinnerRevealed(true);
    //     setShouldShowAnimation(true);
    //     setShowCelebration(true);
    //     setShowLiveModalMobile(true);
        
    //     setTimeout(() => {
    //         if (winnerSectionRef.current) {
    //             const elementTop = winnerSectionRef.current.getBoundingClientRect().top + window.scrollY;
    //             const offsetPosition = elementTop - 99;
                
    //             window.scrollTo({
    //                 top: offsetPosition,
    //                 behavior: 'smooth'
    //             });
    //         }
    //     }, 100);
    // };

    const metaData = {
        authorName: "ACKO DRIVE",
        authorType: "ACKO DRIVE",
        duration: "127",
        mediaSource: "S3",
        primaryText: "ACKO Drive Car Drop Winner",
        secondaryText: "",
        thumbnail: `${assetLoader()}/images/winnerThumbnail.png`,
        title: "ACKO Drive Car Drop Winner",
    };

    const metaDataJanuary = {
        authorName: "ACKO DRIVE",
        authorType: "ACKO DRIVE",
        duration: "127",
        mediaSource: "S3",
        primaryText: "ACKO Drive Car Drop Winner",
        secondaryText: "",
        thumbnail: isMobile ? `${assetLoader()}/images/CarDrop/jan_car_drop_winner_vertical_thumbnail.webp` : `${assetLoader()}/images/CarDrop/jan_car_drop_winner_horizontal_thumbnail.webp`,
        title: "ACKO Drive Car Drop Winner",
    };

    const videoUrl = "https://ackodrive-prod.ackoassets.com/video/generic/jan_car_drop_winner_vertical.m3u8";
    const videoDesktopUrl = "https://ackodrive-prod.ackoassets.com/video/generic/jan_car_drop_winner_horizontal.m3u8";
    const videoNovemberUrl ="https://ackodrive-prod.ackoassets.com/video/generic/dec_car_drop_winner_vertical.m3u8";

    const handleButtonClick = () => {
        showLoader();
        router.push('/');
    }

    const handleWinnerClick = () => {
        // Already on winner declaration page, scroll to top or do nothing
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // const handlePlanningContinue = async () => {
    //     if (!selectedPlanningOption) return;

    //     if (selectedPlanningOption === 'next_3_months' || selectedPlanningOption === 'next_6_months') {
    //         try {
    //             const ipCity = await getCityFromIp();
    //             await postLead({
    //                 phone: userProfile?.phone || userProfile?.contact_no,
    //                 city: cityName,
    //                 ip_city: ipCity,
    //                 buying_period: selectedPlanningOption === 'next_3_months' ? 'Within the next 3 months' : 'Within the next 6 months',
    //                 mx_source: 'car_drop_winner_declaration'
    //             });
    //         } catch (error) {
    //             console.error('Lead capture error:', error);
    //         }
    //     }

    //     await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
    //         pageType: PAGE_TYPES.CAR_DROP_WINNER_DECLARATION_PAGE,
    //         additionalProps: {
    //             logged_in: isLoggedIn ? 'Yes' : 'No',
    //             phone: userProfile?.phone || userProfile?.contact_no,
    //             campaign_name: `${getMonthFromDate(activeCampaignData?.startDate)} Car Drop`,
    //             action_type: 'tap_btn',
    //             action_name:'tap_btn_car_drop_intent',
    //             city_name: cityName,
    //             intent: selectedPlanningOption === 'next_3_months' ? 'Within the next 3 months' : selectedPlanningOption === 'next_6_months' ? 'Within the next 6 months' : 'Not any time soon',
    //         },
    //     });

    //     await postIntentCaptured(activeCampaignData?.id, userId);
    //     setShowPlanningModal(false);
    //     setSelectedPlanningOption('');
    // };

    return (
        <div className={styles.contentSectionWrapper}>
            {/* <div className={styles.CWDpriceSection}>
                <Text className={styles.CWDpriceSectionTitle}>The prizes don&apos;t stop there</Text>
                <Image
                    src={`${assetLoader()}/images/CarDrop/CarDropCashback_${isMobile ? 'Mobile':'Desktop'}.webp`}
                    alt="About AD Car Drop"
                    fill
                    loading='lazy'
                    className={styles.CWDpriceSectionImage}
                />
                <Text className={styles.CWDpriceSectionDesp}>Valid for Express delivery car bookings on ACKO Drive made between 1st-31st Jan 2026.</Text>
            </div> */}
            {/* {isMobile && !isWinnerRevealed && (
                <div className={styles.CWDPreRevealSection}>
                    <Text className={styles.CWDPreRevealSectionHeading}>Meet our<br/><span>December Car Drop Winner</span></Text>
                    <Button 
                        className={styles.CWDPreRevealSectionButton}
                        onClick={handleRevealWinner}
                    >
                        Reveal the winner
                    </Button>
                </div>
            )} */}
            {/* {isMobile && showCelebration && (
                <div className={styles.celebrationOverlay}>
                    <div 
                        ref={lottieLeftRef}
                        className={styles.celebrationAnimationLeft}
                    />
                    <div 
                        ref={lottieRightRef}
                        className={styles.celebrationAnimationRight}
                    />
                </div>
            )} */}
            <div 
                // ref={winnerSectionRef}
                className={`${styles.CWDwinnerUserSection}`}
                // className={`${styles.CWDwinnerUserSection} ${isMobile && shouldShowAnimation ? styles.CWDwinnerUserSectionZoomIn : ''}`}
            >
                <Text className={styles.CWDwinnerUserSectionHeading}>Meet our<br/><span>January Car Drop Winner</span></Text>
                <div className={styles.CWDwinnerUserSectionContent}>
                    <VideoModalWrapper
                        metaData={metaDataJanuary}
                        videoUrl={videoUrl}
                        desktopVideoUrl={videoDesktopUrl}
                    >
                        <Image 
                            src={`${assetLoader()}/images/CarDrop/winnerPersonMobThumbnail_January.webp`}
                            alt='Winner Thumbnail'
                            fill
                            className={styles.CWDwinnerSectionUserImage}
                        />
                    </VideoModalWrapper>
                    <Text className={styles.CWDwinnerSectionUserName}>Martin P David</Text>
                    <Text className={styles.CWDwinnerSectionUserCity}>Bengaluru, Karnataka</Text>
                    <Image 
                        src={`${assetLoader()}/images/CarDrop/ACR_CarName_Jan.webp`}
                        alt='Winner Thumbnail'
                        fill
                        className={styles.CWDwinnerSectionCarImage}
                    />
                    <Text className={styles.CWDwinnerSectionDescp}>“I honestly thought this was a prank. Turns out I really won an MG Hector! Thank you ACKO Drive for this unbelievable surprise that I&apos;ll never forget.”</Text>
                </div>
            </div>
            {
                completedCampaign && completedCampaign?.metadata?.winnerName && completedCampaign?.metadata?.productRevealDate &&
                <div className={styles.winnerSection}>
                    <Text className={styles.winnerSectionDescMobile}>Previous our Car Drop Winners</Text>
                    <Text className={styles.winnerSectionDescDesktop}>Previous our Car Drop Winners</Text>

                    {
                        sortedCompletedCampaigns && sortedCompletedCampaigns.length > 0 && (
                            <div className={styles.winnerSectionButtonsContainer}>
                                {
                                    sortedCompletedCampaigns.map((campaign: any, index: number) => (
                                        <Button
                                            className={`${styles.winnerSectionButtons} ${selectedCampaignIndex === index ? styles.winnerSectionButtonsActive : ''}`}
                                            onClick={() => setSelectedCampaignIndex(index)}
                                            key={index}
                                        >
                                            {getMonthFromDate(campaign?.startDate)}
                                        </Button>
                                    ))
                                }
                            </div>
                        )
                    }
                    <div className={styles.winnerDetailsSection}>
                        <VideoModalWrapper cityName={cityName} metaData={selectedMetaData} videoUrl={selectedVideoUrl} desktopVideoUrl={selectedDesktopVideoUrl}>
                            <Image
                                src={`${assetLoader()}/images/CarDrop/carDropWinnerPerson${getMonthFromDate(selectedCampaign?.startDate)}.webp`}
                                alt="About AD Car Drop"
                                fill
                                loading='lazy'
                                className={styles.winnerSectionImage}
                            />
                        </VideoModalWrapper>
                        <div className={styles.winnerDetailsInnerSection}>
                            <div className={styles.winnerDetails}>
                                <Text className={styles.winnerName}>{selectedWinnerName}</Text>
                                <Text className={styles.winnerLocation}>{selectedWinnerCity}</Text>
                            </div>

                            {isSelectedCampaignPreviousMonth && (!selectedCashbackWinnerData || selectedCashbackWinnerData.length === 0) && (
                                <>
                                    <hr className={styles.winnerSectionMiddleLine} />
                                    <div className={styles.winnerSectionInnerContent}>
                                        <Text className={styles.winnerSectionInnerContentHead}>
                                            Additional reward exclusively for {getMonthFromDate(selectedCampaign?.startDate)} participants
                                        </Text>
                                        <Image
                                            src={`${assetLoader()}/images/CarDrop/CarDropCashback_${isMobile ? 'Mobile' : 'Desktop'}.webp`}
                                            alt="About AD Car Drop"
                                            fill
                                            loading='lazy'
                                            className={styles.winnerSectionInnerContentImage}
                                        />
                                        <Text className={styles.winnerSectionInnerContentSubText}>
                                            Valid for Express Delivery cars booked on ACKO Drive between 1st–31st Jan 2026
                                        </Text>
                                    </div>
                                    <Button className={styles.winnerButton} onClick={handleWinnerClick}>
                                        See details
                                    </Button>
                                </>
                            )}

                            {selectedCashbackWinnerData && selectedCashbackWinnerData.length > 0 && (
                                <>
                                    <hr className={styles.winnerSectionMiddleLine} />
                                    <div className={styles.winnerCashbackSection}>
                                        <Text className={styles.winnerCashbackSectionText}>Lucky car buyers who won <span>{selectedCashbackAmount} cashback</span></Text>
                                        <div className={styles.winnerCashbackSectionDetails}>
                                            {
                                                selectedCashbackWinnerData.map((winner: any, index: number) => (
                                                    <div key={index} className={styles.winnerCashbackSectionUserDetails}>
                                                        <Text className={styles.winnerCashbackSectionUserName}>{winner.winnerName}</Text>
                                                        <Text className={styles.winnerCashbackSectionUserCarAndCity}>{winner.carName} • {winner.winnerCity}</Text>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    </div>
                                    {/* {isSelectedCampaignPreviousMonth && (
                                        <Button className={styles.winnerButton} onClick={handleWinnerClick}>
                                            See details
                                        </Button>
                                    )} */}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            }

            {/* {
                (isMobile ? showLiveModalMobile : true) && activeCampaignData && activeCampaignData?.status === "ACTIVE" && */}
                <div className={styles.liveModalContainer}>
                    <div className={styles.liveModalContent}>
                        <Image
                            src={isMobile ? `${assetLoader()}/images/CarDrop/lastCarDropModalMobile.webp` : `${assetLoader()}/images/CarDrop/lastCarDropModalDesktop.webp`}
                            alt='arrow icon'
                            fill
                            loading='eager'
                            className={styles.lastCarDropModalImage}
                        />
                        {/* <video
                            src={`${assetLoader()}/images/CarDrop/CarDropACR_Jan.webm`}
                            className={styles.liveModalContentImage}
                            autoPlay
                            loop
                            muted
                            playsInline
                            preload="auto"
                        />
                        <div className={styles.liveModalInnerContent}>
                            <Image
                                src={`${assetLoader()}/images/CarDropLogo.svg`}
                                alt='arrow icon'
                                fill
                                loading='eager'
                                className={styles.liveModalInnerContentImage}
                            /> 
                            <div className={styles.liveModalInnerContentLive}>
                                <Text className={styles.liveModalInnerContentLiveText}>{activeCampaignData?.startDate ? getMonthFromDate(activeCampaignData?.startDate) : 'New'} edition is</Text>
                                <div className={styles.liveModalInnerContentLiveContainer}>
                                    <div className={styles.liveModalInnerContentLiveGreen} />
                                    <Text className={styles.liveModalInnerContentLiveContainerText}>Live</Text>
                                </div>
                            </div>
                        </div> */}
                    </div>
                    <Text className={styles.liveModalContentButtonHEading}>Looking for a new car?</Text>
                    <Text className={styles.liveModalContentButtonsubText}>We&apos;ve got exciting offers on new cars.</Text>
                    <Button
                        className={styles.liveModalContentButton}
                        onClick={handleButtonClick}
                    >
                        {/* {
                            isUserParticipated ? 'View your entry details' : `Join the ${activeCampaignData?.startDate ? getMonthFromDate(activeCampaignData?.startDate) : ''} Car Drop`
                        } */}
                        Explore ACKO Drive
                    </Button>
                </div>
            {/* } */}

            {/* {shouldShowPlanningModal && (
                <Modal
                    isOpen={shouldShowPlanningModal}
                    onClose={() => {}}
                    overlayClassName={styles.planningModalOverlay}
                    contentClassName={styles.planningModalContent}
                    headerClassName={styles.planningModalHeaderClassName}
                    showCloseButton={false}
                >
                    <div className={styles.planningModalContainer}>
                        <Text className={styles.planningModalHeading}>
                            When are you planning to buy a new car?
                        </Text>
                        <div className={styles.planningOptionsContainer}>
                            {planningOptions.map((option) => (
                                <button
                                    key={option.id}
                                    className={`${styles.planningOption} ${selectedPlanningOption === option.id ? styles.planningOptionSelected : ''}`}
                                    onClick={() => setSelectedPlanningOption(option.id)}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                        <Button 
                            className={`${styles.planningContinueButton} ${!selectedPlanningOption ? styles.planningContinueButtonDisabled : ''}`}
                            onClick={handlePlanningContinue}
                            disabled={!selectedPlanningOption}
                        >
                            Continue
                        </Button>
                    </div>
                </Modal>
            )} */}
        </div>
    )
}

export default ContentSection
